import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { authUser, edgeInset } from '../config';
import { GEAR_BOTTOM } from './adminChrome';

// AdminPanel — the single hub for the World's tuning tools. A ⚙ button (admins
// only; ?admin forces it for testing) opens a menu; picking a tool sets
// worldStore.adminTool, and exactly ONE panel / in-scene edit mode shows at a
// time (CarSizePanel, GuardPanel, PerformerPanel, NpcPlacePanel, RoutesPanel,
// CameraPanel, billboard placement). Replaces the old scattered ?<key>=edit
// panels; those params still deep-link (they seed adminTool on load).
const SHOW = (() => {
  try { return authUser().isAdmin || new URLSearchParams(window.location.search).get('admin') != null; }
  catch { return false; }
})();

// Some tools only make sense in a context; the note is just a hint (they no-op
// harmlessly elsewhere — e.g. Guard/Performer live in the concert hall).
// Grouped, because the list had grown into a flat wall of twelve where four of
// the entries were the same job in different places — "who is standing where" —
// and you had to already know that to find the right one.
const GROUPS = [
  {
    name: 'NPCs',
    tools: [
      { key: 'place', label: 'Street residents', note: '?city=model' },
      { key: 'iresident', label: 'Inside a room', note: 'in an interior' },
      { key: 'guard', label: 'Concert guard', note: '?city=model' },
      { key: 'performer', label: 'Stage performer', note: 'concert hall' },
    ],
  },
  {
    name: 'Surfaces',
    tools: [
      // THE unified room arranger — numbers every placed thing in the room
      // (panels, plaques, cards) and saves rows. The per-type tools below it
      // remain for their content-specific jobs (artwork, shelf slots).
      { key: 'placeables', label: 'Room placeables', note: 'in an interior' },
      { key: 'placements', label: 'Ad placements', note: 'look at a wall' },
      { key: 'bb', label: 'Billboards', note: '?city=model' },
      { key: 'plaques', label: 'Record wall', note: 'studio' },
      { key: 'cards', label: 'Record sleeves', note: 'record store' },
    ],
  },
  {
    name: 'World',
    tools: [
      { key: 'cam', label: 'Camera / spawn' },
      { key: 'cars', label: 'Car size', note: '?city=model' },
      { key: 'routes', label: 'Road routes', note: '?city=model' },
      { key: 'blocks', label: 'Building grid', note: 'top-down' },
      { key: 'venues', label: 'Venue anchors', note: 'move a venue' },
      // Where the town's NEXT venue can stand (city/siteSurvey): surveys the
      // city's unused buildings and saves them for the venue pipeline.
      { key: 'sites', label: 'Sites', note: 'free buildings' },
      { key: 'guard', label: 'Bouncers / door', note: 'gate any building' },
      { key: 'portal', label: 'Portals / entrances', note: 'walk-in doors' },
      { key: 'story', label: 'THE COME UP', note: 'run any act' },
    ],
  },
];

const wrap = {
  position: 'fixed', left: edgeInset(16, 'left'),
  // Raised off the bottom row so the ⚙ never sits on the now-playing bar
  // (desktop, ~52px) or the movement joystick (touch, ~154px). adminChrome
  // keeps the left-hand panels above this same line.
  bottom: GEAR_BOTTOM,
  zIndex: 50, pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
};
const gear = {
  width: 46, height: 46, borderRadius: 999, cursor: 'pointer', fontSize: 20, lineHeight: '46px',
  background: 'rgba(16,10,6,0.85)', border: '1px solid rgba(255,178,94,0.5)', color: '#ffd27d',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', textAlign: 'center', padding: 0,
};
const menu = {
  marginBottom: 8, width: 184, background: 'rgba(16,10,6,0.92)', border: '1px solid rgba(255,178,94,0.4)',
  borderRadius: 12, padding: '8px 8px 10px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  // The list is taller than a laptop window (2026-11-01, Khari: "I cannot close
  // them unless I stretch my screen beyond the UI"): it scrolls inside the
  // window, from just under the top down to the ⚙.
  maxHeight: `calc(100dvh - ${GEAR_BOTTOM} - 46px - 24px)`, overflowY: 'auto', overscrollBehavior: 'contain', boxSizing: 'border-box',
};

export default function AdminPanel() {
  const tool = useWorldStore((s) => s.adminTool);
  const setTool = useWorldStore((s) => s.setAdminTool);
  const planView = useWorldStore((s) => s.planView);
  const setPlanView = useWorldStore((s) => s.setPlanView);
  const [open, setOpen] = useState(false);
  if (!SHOW) return null;

  const active = GROUPS.flatMap((g) => g.tools).find((t) => t.key === tool) || null;
  return (
    // OPEN, IT IS ON TOP (72: above every tool panel, the highest at 68): a
    // tool's panel on the left (Ad placements, 300px wide from the top-left) used
    // to lie over this menu, which is how a tool closes.
    <div style={{ ...wrap, zIndex: open ? 72 : wrap.zIndex }}>
      {open && (
        <div style={menu}>
          <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, color: '#ffd27d', margin: '2px 4px 8px' }}>
            World tools
          </div>
          {active && (
            <button
              onClick={() => { setTool(active.key); setOpen(false); }}
              style={{
                display: 'block', width: '100%', margin: '0 0 6px', padding: '8px 9px', borderRadius: 8, cursor: 'pointer',
                font: '700 12.5px system-ui', border: '1px solid rgba(255,125,125,0.45)', textAlign: 'left',
                background: 'rgba(255,125,125,0.12)', color: '#ffd6d6',
              }}
            >
              ✕ Close {active.label}
            </button>
          )}
          {GROUPS.map((group) => (
            <div key={group.name}>
              <div style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.5, color: '#ffd27d', margin: '10px 4px 4px' }}>
                {group.name}
              </div>
              {group.tools.map((t) => {
                const on = tool === t.key;
                return (
              <button
                key={t.key}
                onClick={() => { setTool(t.key); setOpen(false); }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
                  margin: '3px 0', padding: '8px 9px', borderRadius: 8, cursor: 'pointer', font: '600 12.5px system-ui',
                  border: '1px solid rgba(255,178,94,0.3)', textAlign: 'left',
                  background: on ? '#ffb25e' : 'rgba(255,178,94,0.1)', color: on ? '#1c1206' : '#ffe9c4',
                }}
              >
                <span>{t.label}</span>
                {t.note && <span style={{ fontSize: 9.5, opacity: on ? 0.7 : 0.45 }}>{on ? 'active' : t.note}</span>}
              </button>
                );
              })}
            </div>
          ))}
          {/* A LENS, NOT A TOOL — so it sits below the list with its own toggle.
              Every entry above owns the click and only one thing can; this owns
              the camera, which all of them still need. It is meant to be ON
              while you use the building grid, not instead of it. */}
          <div style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.5, color: '#ffd27d', margin: '12px 4px 4px' }}>
            View
          </div>
          <button
            onClick={() => setPlanView(!planView)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
              margin: '3px 0', padding: '8px 9px', borderRadius: 8, cursor: 'pointer', font: '600 12.5px system-ui',
              border: '1px solid rgba(157,255,125,0.35)', textAlign: 'left',
              background: planView ? '#9dff7d' : 'rgba(157,255,125,0.1)', color: planView ? '#0c1c06' : '#e6ffd9',
            }}
          >
            <span>Build view</span>
            <span style={{ fontSize: 9.5, opacity: planView ? 0.7 : 0.45 }}>{planView ? 'on' : 'top-down, flat'}</span>
          </button>
          <div style={{ fontSize: 10, opacity: 0.55, color: '#ffe9c4', margin: '6px 4px 0', lineHeight: 1.4 }}>
            Pick a tool to open its panel; tap ⚙ to switch or close it. Build view
            is a camera — leave it on while you place.
          </div>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} title="World admin" style={gear}>⚙</button>
    </div>
  );
}
